import { SeasonModel } from "models"
import {
	DayWisePricingGroupLocal,
	IHotelContractValidationError,
	PeakDateRangeLocal,
	SeasonDateRangeLocal,
} from "component/hotelContract/hotelContractTypes"

export const HOTEL_CONTRACT_ERROR_MESSAGES = {
	contractDatesRequired: "Contract start date and end date are required.",
	seasonNameRequired: "Season name is required.",
	seasonNameDuplicate: "Season name must be unique within this contract.",
	seasonNameRequiredWithRanges: "Season name is required when date ranges are added.",
	seasonOrDayWisePricingRequired: "Add at least one season or enable day-wise pricing.",
	dayGroupInRateTableRequired: "Enable \"Day Group in Rate Table\" when no season is added.",
	dayGroupSurchargeNeedsSeason:
		"Day group surcharge needs at least one season. Uncheck \"Surcharge?\" and enable \"Day Group in Rate Table\".",
	perPersonSeasonalSurchargeNeedsSeason: "Per-person seasonal surcharges need at least one season.",
	seasonalExtraBedNeedsSeason: "Season-wise extra bed price needs at least one season.",
	dayGroupNameRequired: "Group name is required.",
	mealPlanRequired: "At least one meal plan must be selected.",
	gitFitRoomsInvalid: "Rooms must be a positive whole number.",
	gitFitPersonsInvalid: "Persons must be a positive whole number.",
	peakDateRangeRequired: "Add at least one peak date range.",
}

const isBlank = (value: string | null | undefined): boolean => !value || value.trim() === ""

const isRemainingRange = (range: SeasonDateRangeLocal): boolean => range.range_id.startsWith("remaining_")

/**
 * A contract has "no season" when its only season row has an empty name.
 * The season list always keeps at least one row, so an empty single row means the user skipped seasons.
 */
export const isContractWithoutSeason = (seasons: SeasonModel[]): boolean => {
	if (seasons.length === 0) return true
	return seasons.length === 1 && isBlank(seasons[0].season_name)
}

/** "+ Season" stays blocked while any season row has an empty name. */
export const canAddSeason = (seasons: SeasonModel[]): boolean => seasons.every((season) => !isBlank(season.season_name))

/** True when every day group is shown in the rate table (mirrors the "Enable Day Group in Rate Table" checkbox). */
export const isDayGroupInRateTableEnabled = (dayWiseGroups: DayWisePricingGroupLocal[]): boolean =>
	dayWiseGroups.length > 0 && dayWiseGroups.every((group) => group.flag_enable_day_group_in_rate_table)

/** A season row with an empty name that already has date data or the "Remaining Dates" flag. */
const hasDateDataWithoutSeasonName = (seasons: SeasonModel[], seasonDateRanges: SeasonDateRangeLocal[]): boolean =>
	seasons.some((season) => {
		if (!isBlank(season.season_name)) return false
		if (season.flag_remaining_season) return true
		return seasonDateRanges.some(
			(range) =>
				range.season_id === season.season_id &&
				!isRemainingRange(range) &&
				(!!range.start_date || !!range.end_date),
		)
	})

export interface ISeasonDependencyInput {
	seasons: SeasonModel[]
	seasonDateRanges: SeasonDateRangeLocal[]
	useDayWisePricing: boolean
	dayWiseGroups: DayWisePricingGroupLocal[]
	enablePerPersonSeasonalSurcharges: boolean
	seasonalExtraBedPricing: boolean
}

/** Errors for settings that need at least one season. Returns [] when the contract has a season. */
export const getSeasonDependencyErrors = (input: ISeasonDependencyInput): IHotelContractValidationError[] => {
	const errors: IHotelContractValidationError[] = []
	if (!isContractWithoutSeason(input.seasons)) return errors

	if (hasDateDataWithoutSeasonName(input.seasons, input.seasonDateRanges)) {
		errors.push({ key: "seasonName", field: "Season", message: HOTEL_CONTRACT_ERROR_MESSAGES.seasonNameRequiredWithRanges })
	}

	if (input.enablePerPersonSeasonalSurcharges) {
		errors.push({
			key: "perPersonSeasonalSurcharge",
			field: "Enable Per-Person Seasonal Surcharges",
			message: HOTEL_CONTRACT_ERROR_MESSAGES.perPersonSeasonalSurchargeNeedsSeason,
		})
	}

	if (!input.useDayWisePricing) {
		errors.push({ key: "dayWisePricing", field: "Day-Wise Pricing", message: HOTEL_CONTRACT_ERROR_MESSAGES.seasonOrDayWisePricingRequired })
	} else if (input.dayWiseGroups.some((group) => group.flag_surchange_applied)) {
		// Checking "Surcharge?" always turns "Day Group in Rate Table" off, so one combined message replaces both errors.
		errors.push({ key: "dayWisePricing", field: "Day-Wise Pricing", message: HOTEL_CONTRACT_ERROR_MESSAGES.dayGroupSurchargeNeedsSeason })
	} else if (!isDayGroupInRateTableEnabled(input.dayWiseGroups)) {
		errors.push({ key: "dayWisePricing", field: "Day-Wise Pricing", message: HOTEL_CONTRACT_ERROR_MESSAGES.dayGroupInRateTableRequired })
	}

	if (input.seasonalExtraBedPricing) {
		errors.push({
			key: "extraBedPricing",
			field: "Extra Bed Pricing",
			message: HOTEL_CONTRACT_ERROR_MESSAGES.seasonalExtraBedNeedsSeason,
		})
	}

	return errors
}

export interface IHotelContractErrorsInput extends ISeasonDependencyInput {
	contractDateErrors: string[]
	contractStartDate: string
	contractEndDate: string
	rateStructureType: number | null
	selectedMealPlans: number[]
	gitFitMinRoomCount: number
	gitFitMinPersonCount: number
	enablePeakDateSurcharges: boolean
	peakDateRanges: PeakDateRangeLocal[]
	getDateRangeErrors: (rangeId: string, startDate: string, endDate: string) => string[]
	getPeakDateRangeErrors: (rangeId: string, startDate: string, endDate: string) => string[]
}

const quoteName = (name: string | null | undefined, fallback: string): string => (isBlank(name) ? fallback : `"${name!.trim()}"`)

const uniqueErrors = (errors: IHotelContractValidationError[]): IHotelContractValidationError[] => {
	const seen = new Set<string>()
	return errors.filter((error) => {
		const id = `${error.key}|${error.message}`
		if (seen.has(id)) return false
		seen.add(id)
		return true
	})
}

/** Every validation error of one hotel contract, in form order. Empty list = contract is valid. */
export const collectHotelContractErrors = (input: IHotelContractErrorsInput): IHotelContractValidationError[] => {
	const errors: IHotelContractValidationError[] = []
	const withoutSeason = isContractWithoutSeason(input.seasons)

	input.contractDateErrors.forEach((message) => errors.push({ key: "contractDates", field: "Contract Dates", message }))
	if (!input.contractStartDate || !input.contractEndDate) {
		errors.push({ key: "contractDates", field: "Contract Dates", message: HOTEL_CONTRACT_ERROR_MESSAGES.contractDatesRequired })
	}

	if (!withoutSeason) {
		const seasonNames = input.seasons.filter((s) => !isBlank(s.season_name)).map((s) => s.season_name!.trim().toLowerCase())
		if (input.seasons.some((s) => isBlank(s.season_name))) {
			errors.push({ key: "seasonName", field: "Season", message: HOTEL_CONTRACT_ERROR_MESSAGES.seasonNameRequired })
		}
		if (seasonNames.length !== new Set(seasonNames).size) {
			errors.push({ key: "seasonName", field: "Season", message: HOTEL_CONTRACT_ERROR_MESSAGES.seasonNameDuplicate })
		}

		input.seasons
			.filter((season) => !season.flag_remaining_season)
			.forEach((season) => {
				const seasonLabel = quoteName(season.season_name, "season")
				const ranges = input.seasonDateRanges.filter((r) => r.season_id === season.season_id && !isRemainingRange(r))
				if (ranges.length === 0) {
					errors.push({
						key: "seasonDateRanges",
						field: "Season Date Ranges",
						message: `Add at least one date range for ${seasonLabel}.`,
					})
					return
				}
				ranges.forEach((range) => {
					if (!range.start_date || !range.end_date) return
					input.getDateRangeErrors(range.range_id, range.start_date, range.end_date).forEach((message) =>
						errors.push({ key: "seasonDateRanges", field: "Season Date Ranges", message: `${seasonLabel}: ${message}` }),
					)
				})
			})
	}

	if (input.useDayWisePricing) {
		input.dayWiseGroups.forEach((group) => {
			if (isBlank(group.day_wise_pricing_group_name)) {
				errors.push({ key: "dayWisePricing", field: "Day-Wise Pricing", message: HOTEL_CONTRACT_ERROR_MESSAGES.dayGroupNameRequired })
			}
			if (group.days.length === 0) {
				errors.push({
					key: "dayWisePricing",
					field: "Day-Wise Pricing",
					message: `Select at least one day for ${quoteName(group.day_wise_pricing_group_name, "each day group")}.`,
				})
			}
		})
	}

	errors.push(...getSeasonDependencyErrors(input))

	if (input.rateStructureType === 2 && input.selectedMealPlans.length === 0) {
		errors.push({ key: "rateStructure", field: "Rate Structure", message: HOTEL_CONTRACT_ERROR_MESSAGES.mealPlanRequired })
	}
	if (input.rateStructureType === 3) {
		if (input.gitFitMinRoomCount <= 0 || !Number.isInteger(input.gitFitMinRoomCount)) {
			errors.push({ key: "rateStructure", field: "Rate Structure", message: HOTEL_CONTRACT_ERROR_MESSAGES.gitFitRoomsInvalid })
		}
		if (input.gitFitMinPersonCount <= 0 || !Number.isInteger(input.gitFitMinPersonCount)) {
			errors.push({ key: "rateStructure", field: "Rate Structure", message: HOTEL_CONTRACT_ERROR_MESSAGES.gitFitPersonsInvalid })
		}
	}

	if (input.enablePeakDateSurcharges) {
		if (!input.peakDateRanges.some((range) => range.start_date && range.end_date)) {
			errors.push({ key: "peakDateSurcharges", field: "Peak Date Surcharges", message: HOTEL_CONTRACT_ERROR_MESSAGES.peakDateRangeRequired })
		}
		input.peakDateRanges.forEach((range) => {
			if (!range.start_date || !range.end_date) return
			input.getPeakDateRangeErrors(range.range_id, range.start_date, range.end_date).forEach((message) =>
				errors.push({ key: "peakDateSurcharges", field: "Peak Date Surcharges", message }),
			)
		})
	}

	return uniqueErrors(errors)
}

/** First error message for one form section, used for inline display. */
export const getFirstErrorMessage = (
	errors: IHotelContractValidationError[],
	key: IHotelContractValidationError["key"],
): string | undefined => errors.find((error) => error.key === key)?.message
